import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { http, currency, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Loader2, Megaphone, Plus, Trash2, MessageCircle, Mail, Users, Image as ImageIcon,
  Video, Link as LinkIcon, X, ArrowLeft, Copy, CheckSquare, Square, Send, Search, Save,
} from "lucide-react";
import { toast } from "sonner";

// Marketing campaigns admin — create campaigns with a message + media + product references,
// then broadcast to customers via WhatsApp/email. Tracks which customer received which
// campaign to prevent duplicate sends.
export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // opened campaign
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/campaigns");
      setCampaigns(data || []);
    } catch {
      toast.error("No se pudieron cargar las campañas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCampaign = async (id) => {
    try {
      const { data } = await http.get(`/admin/campaigns/${id}`);
      setSelected(data);
    } catch {
      toast.error("No se pudo abrir la campaña");
    }
  };

  const removeCampaign = async (id) => {
    if (!window.confirm("¿Eliminar la campaña? Se perderá el historial de envíos.")) return;
    try {
      await http.delete(`/admin/campaigns/${id}`);
      toast.success("Campaña eliminada");
      setSelected(null);
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  if (creating) {
    return (
      <CampaignForm
        onCancel={() => setCreating(false)}
        onSaved={(c) => { setCreating(false); load(); setSelected(c); }}
      />
    );
  }

  if (selected) {
    return (
      <CampaignDetail
        campaign={selected}
        onBack={() => { setSelected(null); load(); }}
        onDelete={() => removeCampaign(selected.campaign_id)}
        onChange={(c) => setSelected(c)}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-campaigns">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5" />
        <h2 className="font-display text-2xl">Campañas de marketing</h2>
        <div className="text-xs font-mono uppercase text-neutral-500 ml-auto">
          Campañas proactivas · sin duplicar envíos
        </div>
      </div>

      <div>
        <Button
          onClick={() => setCreating(true)}
          className="rounded-none bg-black hover:bg-neutral-800 text-white"
          data-testid="admin-campaign-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nueva campaña
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="border border-neutral-200 p-8 text-center text-sm text-neutral-500 italic">
          Aún no hay campañas. Crea la primera para promocionar tus nuevos productos.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {campaigns.map((c) => (
            <button
              key={c.campaign_id}
              onClick={() => openCampaign(c.campaign_id)}
              className="text-left border border-neutral-200 hover:border-black transition-colors p-4 space-y-2"
              data-testid={`admin-campaign-card-${c.campaign_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-display text-lg leading-tight">{c.name}</div>
                <div className="text-[10px] font-mono text-neutral-500 shrink-0">
                  {new Date(c.created_at).toLocaleDateString("es-MX", { timeZone: "America/Monterrey" })}
                </div>
              </div>
              <div className="text-xs text-neutral-600 line-clamp-3 whitespace-pre-wrap">{c.message}</div>
              <div className="flex flex-wrap gap-2 pt-1 text-[10px] font-mono uppercase text-neutral-600">
                <span className="px-1.5 py-0.5 bg-neutral-100">
                  <Send className="w-3 h-3 inline mr-1" />
                  {c.sent_count} enviados
                </span>
                {c.media_count > 0 && (
                  <span className="px-1.5 py-0.5 bg-neutral-100">
                    <ImageIcon className="w-3 h-3 inline mr-1" />
                    {c.media_count} media
                  </span>
                )}
                {c.product_count > 0 && (
                  <span className="px-1.5 py-0.5 bg-neutral-100">
                    {c.product_count} producto{c.product_count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------- Create/Edit form ----------
function CampaignForm({ campaign = null, onCancel, onSaved }) {
  const isEdit = !!campaign;
  const [name, setName] = useState(campaign?.name || "");
  const [message, setMessage] = useState(
    campaign?.message ||
      "¡Hola {nombre}! Tenemos nuevos productos que creemos te van a encantar 🎉 Escríbenos para ver diseños y precios especiales para clientes."
  );
  const [media, setMedia] = useState(campaign?.media || []);
  const [productIds, setProductIds] = useState(campaign?.product_ids || []);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extUrl, setExtUrl] = useState("");
  const [extKind, setExtKind] = useState("url");
  const fileRef = useRef(null);

  useEffect(() => {
    http.get("/products").then(({ data }) => setProducts(data || [])).catch(() => {});
  }, []);

  const filteredProducts = useMemo(() => {
    const needle = productSearch.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(needle));
  }, [products, productSearch]);

  const toggleProduct = (id) => {
    setProductIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const isVideo = /video/.test(file.type);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMedia((m) => [...m, {
        kind: isVideo ? "video" : "image",
        url: `${API}/files/${data.storage_path}`,
        caption: file.name,
      }]);
      toast.success("Archivo subido");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addExtUrl = () => {
    const url = extUrl.trim();
    if (!url) return;
    setMedia((m) => [...m, { kind: extKind, url, caption: "" }]);
    setExtUrl("");
  };

  const removeMedia = (i) => setMedia((m) => m.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return toast.error("Ingresa un nombre para la campaña");
    if (!message.trim()) return toast.error("El mensaje no puede estar vacío");
    setSaving(true);
    try {
      const payload = { name: name.trim(), message, media, product_ids: productIds };
      const { data } = isEdit
        ? await http.put(`/admin/campaigns/${campaign.campaign_id}`, payload)
        : await http.post("/admin/campaigns", payload);
      toast.success(isEdit ? "Campaña actualizada" : "Campaña creada");
      onSaved(data);
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-campaign-form">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 hover:bg-neutral-100" data-testid="admin-campaign-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h2 className="font-display text-2xl">{isEdit ? "Editar campaña" : "Nueva campaña"}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-neutral-500">Nombre interno</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ej. Promo octubre — gorras nuevas"
              className="rounded-none h-10 mt-1"
              maxLength={120}
              data-testid="admin-campaign-name"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-neutral-500">
              Mensaje (usa <code>{"{nombre}"}</code> para insertar el nombre del cliente)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              maxLength={2000}
              className="w-full mt-1 px-3 py-2 text-sm border border-neutral-300 rounded-none resize-y whitespace-pre-wrap focus:border-black focus:outline-none"
              data-testid="admin-campaign-message"
            />
            <div className="text-[10px] text-neutral-500 mt-0.5">{message.length}/2000</div>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase text-neutral-500">Media (imágenes, videos o enlaces)</label>
            <div className="mt-1 space-y-2">
              {media.length > 0 && (
                <ul className="space-y-1" data-testid="admin-campaign-media-list">
                  {media.map((m, i) => (
                    <li key={i} className="flex items-center gap-2 border border-neutral-200 p-2">
                      {m.kind === "video" ? <Video className="w-4 h-4 text-purple-600" /> :
                        m.kind === "image" ? <ImageIcon className="w-4 h-4 text-blue-600" /> :
                        <LinkIcon className="w-4 h-4 text-neutral-500" />}
                      <div className="min-w-0 flex-1 text-xs truncate">
                        <div className="font-mono uppercase text-[9px] text-neutral-500">{m.kind}</div>
                        <a href={m.url} target="_blank" rel="noreferrer" className="text-neutral-700 truncate block" title={m.url}>{m.url}</a>
                      </div>
                      <button onClick={() => removeMedia(i)} className="text-neutral-500 hover:text-red-600" data-testid={`admin-campaign-media-remove-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/mp4,video/webm,video/quicktime"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="admin-campaign-upload-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-none text-xs"
                  disabled={uploading}
                  data-testid="admin-campaign-upload-btn"
                >
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                  Subir archivo
                </Button>
                <select
                  value={extKind}
                  onChange={(e) => setExtKind(e.target.value)}
                  className="h-8 text-xs px-1.5 border border-neutral-300 bg-white"
                  data-testid="admin-campaign-ext-kind"
                >
                  <option value="url">Enlace</option>
                  <option value="image">Imagen</option>
                  <option value="video">Video</option>
                </select>
                <Input
                  value={extUrl}
                  onChange={(e) => setExtUrl(e.target.value)}
                  placeholder="Pegar URL (YouTube, Drive, etc.)"
                  className="rounded-none h-8 text-xs flex-1 min-w-[220px]"
                  data-testid="admin-campaign-ext-url"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addExtUrl}
                  className="rounded-none text-xs"
                  data-testid="admin-campaign-ext-add"
                >
                  Agregar
                </Button>
              </div>
              <div className="text-[10px] text-neutral-500 italic">
                Tip: WhatsApp genera vista previa automáticamente al primer enlace del mensaje. Añade tu URL de imagen/video y pégala en el mensaje o déjala en media para copiarla después.
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-neutral-500">Productos que incluye la campaña ({productIds.length})</label>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Buscar producto…"
                className="rounded-none h-9 pl-9 text-sm"
                data-testid="admin-campaign-product-search"
              />
            </div>
            <div className="mt-2 border border-neutral-200 max-h-[420px] overflow-y-auto" data-testid="admin-campaign-product-list">
              {filteredProducts.length === 0 ? (
                <div className="p-4 text-xs text-neutral-500 italic text-center">No hay productos</div>
              ) : (
                filteredProducts.map((p) => {
                  const active = productIds.includes(p.product_id);
                  return (
                    <button
                      key={p.product_id}
                      onClick={() => toggleProduct(p.product_id)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 border-b border-neutral-100 last:border-b-0 ${active ? "bg-emerald-50" : "hover:bg-neutral-50"}`}
                      data-testid={`admin-campaign-product-${p.product_id}`}
                    >
                      {active ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4 text-neutral-400" />}
                      <div className="w-8 h-8 bg-neutral-100 shrink-0 overflow-hidden">
                        {p.base_image_url && <img src={p.base_image_url} alt={p.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{p.name}</div>
                        <div className="text-[10px] font-mono text-neutral-500">{currency(p.base_price || 0)}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="rounded-none">Cancelar</Button>
        <Button onClick={save} disabled={saving} className="rounded-none bg-black hover:bg-neutral-800 text-white" data-testid="admin-campaign-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          {isEdit ? "Guardar cambios" : "Crear campaña"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Detail view: send to customers ----------
function CampaignDetail({ campaign, onBack, onDelete, onChange }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showSent, setShowSent] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/customers");
      setCustomers(data || []);
    } catch {
      toast.error("No se pudieron cargar los clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sentKeys = useMemo(() => new Set((campaign.sent_to || []).map((s) => s.customer_key)), [campaign]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return customers.filter((c) => {
      const isSent = sentKeys.has(c.key);
      if (!showSent && isSent) return false;
      if (showSent && !isSent) return false;
      if (!needle) return true;
      return (
        (c.name || "").toLowerCase().includes(needle) ||
        (c.email || "").toLowerCase().includes(needle) ||
        (c.phone || "").toLowerCase().includes(needle)
      );
    });
  }, [customers, sentKeys, showSent, query]);

  const buildMessage = (c) => {
    const first = (c.name || "").split(" ")[0] || "cliente";
    let text = (campaign.message || "").replace(/\{nombre\}/g, first);
    // Append media links so WhatsApp auto-previews the first one
    for (const m of (campaign.media || [])) {
      if (m.url) text += `\n${m.url}`;
    }
    return text;
  };

  const sanitizePhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length === 10) return `52${digits}`;
    return digits;
  };

  const waLink = (c) => {
    const phone = sanitizePhone(c.phone);
    if (!phone) return null;
    return `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(c))}`;
  };

  const mailtoLink = (c) => {
    if (!c.email) return null;
    const subject = `${campaign.name} · Printec del Norte`;
    return `mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMessage(c))}`;
  };

  const markSent = async (c, channel) => {
    setSending((s) => ({ ...s, [c.key]: true }));
    try {
      const { data } = await http.post(`/admin/campaigns/${campaign.campaign_id}/mark-sent`, {
        customer_key: c.key,
        customer_name: c.name || null,
        customer_phone: c.phone || null,
        customer_email: c.email || null,
        channel,
      });
      onChange(data);
      toast.success(`Marcado enviado a ${c.name || c.phone || c.email}`);
    } catch {
      toast.error("No se pudo registrar el envío");
    } finally {
      setSending((s) => ({ ...s, [c.key]: false }));
    }
  };

  const unmarkSent = async (c) => {
    if (!window.confirm("¿Quitar del historial? Podrás volver a enviar la campaña a este cliente.")) return;
    try {
      const { data } = await http.delete(`/admin/campaigns/${campaign.campaign_id}/mark-sent/${encodeURIComponent(c.key)}`);
      onChange(data);
      toast.success("Cliente eliminado del historial de envíos");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const sendAndMark = async (c) => {
    const url = waLink(c);
    if (!url) return toast.error("Este cliente no tiene teléfono");
    window.open(url, "_blank", "noopener,noreferrer");
    await markSent(c, "whatsapp");
  };

  const sendAndMarkEmail = async (c) => {
    const url = mailtoLink(c);
    if (!url) return toast.error("Este cliente no tiene email");
    window.location.href = url;
    await markSent(c, "email");
  };

  const copyMessage = (c) => {
    navigator.clipboard.writeText(buildMessage(c)).then(
      () => toast.success("Mensaje copiado"),
      () => toast.error("No se pudo copiar"),
    );
  };

  if (editing) {
    return (
      <CampaignForm
        campaign={campaign}
        onCancel={() => setEditing(false)}
        onSaved={(c) => { setEditing(false); onChange(c); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-campaign-detail">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <button onClick={onBack} className="p-1.5 hover:bg-neutral-100 mt-1 shrink-0" data-testid="admin-campaign-detail-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="text-xs font-mono uppercase text-neutral-500">Campaña · {new Date(campaign.created_at).toLocaleDateString("es-MX", { timeZone: "America/Monterrey" })}</div>
            <h2 className="font-display text-2xl leading-tight">{campaign.name}</h2>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="rounded-none" data-testid="admin-campaign-edit">Editar</Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="rounded-none text-red-600 border-red-300 hover:bg-red-50" data-testid="admin-campaign-delete">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
          </Button>
        </div>
      </div>

      {/* Message + media preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="border border-neutral-200 p-3 lg:col-span-2">
          <div className="text-[10px] font-mono uppercase text-neutral-500 mb-1">Mensaje</div>
          <div className="text-sm whitespace-pre-wrap">{campaign.message}</div>
          {(campaign.media || []).length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {campaign.media.map((m, i) => (
                <div key={i} className="border border-neutral-200 aspect-square bg-neutral-50 overflow-hidden relative">
                  {m.kind === "image" ? (
                    <img src={m.url} alt={m.caption || `media-${i}`} className="w-full h-full object-cover" />
                  ) : m.kind === "video" ? (
                    <video src={m.url} controls className="w-full h-full object-cover" />
                  ) : (
                    <a href={m.url} target="_blank" rel="noreferrer" className="flex items-center justify-center w-full h-full text-xs text-blue-600 underline p-2 text-center break-all">
                      {m.url}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-neutral-200 p-3">
          <div className="text-[10px] font-mono uppercase text-neutral-500 mb-1">Estado</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>Enviados</span><span className="font-medium">{campaign.sent_to?.length || 0}</span></div>
            <div className="flex justify-between"><span>Productos</span><span className="font-medium">{campaign.product_ids?.length || 0}</span></div>
            <div className="flex justify-between"><span>Media</span><span className="font-medium">{campaign.media?.length || 0}</span></div>
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="border-t border-neutral-200 pt-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Users className="w-4 h-4" />
          <div className="font-display text-lg">{showSent ? "Ya enviados" : "Pendientes de enviar"}</div>
          <div className="flex gap-1 ml-3">
            <button
              onClick={() => setShowSent(false)}
              className={`px-3 py-1 text-xs font-mono uppercase border ${!showSent ? "bg-black text-white border-black" : "text-neutral-600 border-neutral-300"}`}
              data-testid="admin-campaign-tab-pending"
            >
              Pendientes
            </button>
            <button
              onClick={() => setShowSent(true)}
              className={`px-3 py-1 text-xs font-mono uppercase border ${showSent ? "bg-black text-white border-black" : "text-neutral-600 border-neutral-300"}`}
              data-testid="admin-campaign-tab-sent"
            >
              Enviados ({campaign.sent_to?.length || 0})
            </button>
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar cliente…"
              className="rounded-none h-9 pl-9 text-sm"
              data-testid="admin-campaign-detail-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-neutral-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-neutral-200 p-8 text-center text-sm text-neutral-500 italic">
            {showSent ? "Aún no se ha enviado la campaña a ningún cliente." : "No hay clientes pendientes."}
          </div>
        ) : (
          <div className="border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="p-3 text-xs font-mono uppercase text-neutral-500">Cliente</th>
                  <th className="p-3 text-xs font-mono uppercase text-neutral-500">Pedidos anteriores</th>
                  <th className="p-3 text-xs font-mono uppercase text-neutral-500 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isSent = sentKeys.has(c.key);
                  const sentEntry = isSent ? (campaign.sent_to || []).find((s) => s.customer_key === c.key) : null;
                  return (
                    <tr key={c.key} className="border-b border-neutral-100" data-testid={`admin-campaign-row-${c.key}`}>
                      <td className="p-3 align-top">
                        <div className="font-medium">{c.name || <span className="italic text-neutral-400">Sin nombre</span>}</div>
                        {c.email && <div className="text-[11px] text-neutral-600 truncate max-w-[220px]">{c.email}</div>}
                        {c.phone && <div className="text-[11px] text-neutral-600 font-mono">📱 {c.phone}</div>}
                      </td>
                      <td className="p-3 align-top">
                        <div className="text-[11px] text-neutral-500 font-mono uppercase mb-1">{c.order_count} pedido{c.order_count === 1 ? "" : "s"} · {currency(c.total_spent)}</div>
                        <div className="flex flex-wrap gap-1">
                          {(c.products || []).slice(0, 5).map((p, i) => (
                            <span key={i} className={`px-1.5 py-0.5 text-[10px] ${p.is_custom ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-700"}`}>
                              {p.name}
                            </span>
                          ))}
                          {(c.products || []).length > 5 && (
                            <span className="text-[10px] italic text-neutral-500">+{c.products.length - 5} más</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 align-top">
                        {isSent ? (
                          <div className="flex flex-col items-end gap-1">
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 border border-emerald-500 text-emerald-700 text-[10px] font-mono uppercase">
                              ✓ Enviado {sentEntry?.sent_at ? new Date(sentEntry.sent_at).toLocaleDateString("es-MX", { timeZone: "America/Monterrey" }) : ""}
                            </div>
                            <button
                              onClick={() => unmarkSent(c)}
                              className="text-[10px] text-neutral-500 hover:text-red-600 underline"
                              data-testid={`admin-campaign-unmark-${c.key}`}
                            >
                              Quitar del historial
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1 flex-wrap">
                            <button
                              onClick={() => copyMessage(c)}
                              className="p-1.5 border border-neutral-300 hover:bg-neutral-100"
                              title="Copiar mensaje"
                              data-testid={`admin-campaign-copy-${c.key}`}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            {c.phone && (
                              <button
                                onClick={() => sendAndMark(c)}
                                disabled={sending[c.key]}
                                className="p-1.5 border border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                                title="Enviar WhatsApp + marcar enviado"
                                data-testid={`admin-campaign-send-wa-${c.key}`}
                              >
                                {sending[c.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                              </button>
                            )}
                            {c.email && (
                              <button
                                onClick={() => sendAndMarkEmail(c)}
                                disabled={sending[c.key]}
                                className="p-1.5 border border-blue-500 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                title="Abrir email + marcar enviado"
                                data-testid={`admin-campaign-send-mail-${c.key}`}
                              >
                                <Mail className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => markSent(c, "manual")}
                              disabled={sending[c.key]}
                              className="p-1.5 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50"
                              title="Marcar como enviado manualmente"
                              data-testid={`admin-campaign-mark-manual-${c.key}`}
                            >
                              <CheckSquare className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
